const en = {
  // Navigation
  nav: {
    home: 'Home',
    features: 'Features',
    scenarios: 'Use Cases',
    integration: 'Integration',
    docs: 'Docs',
    faq: 'FAQ',
    contact: 'Contact'
  },

  // Hero Section
  hero: {
    badge: '🚀 Next-Gen Digital Currency Payment',
    title: {
      line1: 'Build',
      line2: 'Secure & Stable',
      line3: 'Digital Payments'
    },
    subtitle: 'Support multiple mainstream stablecoins and blockchain networks, providing fast, secure, and low-cost digital currency payment solutions for your business',
    cta: {
      primary: 'Get Started',
      secondary: 'View Docs',
      demo: 'View Demo'
    }
  },

  // Stats Section
  stats: {
    title: 'Platform Overview',
    subtitle: 'Providing reliable digital currency payment infrastructure for global enterprises',
    volumeValue: '$5+',
    volumeUnit: 'Million',
    volume: 'Total Volume',
    successValue: '99.8%',
    success: 'Success Rate',
    networksValue: '15+',
    networksUnit: '',
    networks: 'Supported Networks',
    merchantsValue: '100+',
    merchantsUnit: '',
    merchants: 'Partner Merchants',
    description: 'Real-time data reflecting actual platform performance'
  },

  // Features Section
  features: {
    title: 'Core Capabilities',
    subtitle: 'Comprehensive crypto payment infrastructure for enterprises',
    items: {
      multiCurrency: {
        title: 'Multi-Currency',
        description: 'Support USDT, USDC, DAI and other mainstream stablecoins for different business needs'
      },
      multiChain: {
        title: 'Multi-Chain',
        description: 'Cover 12+ mainstream blockchain networks including Ethereum, BSC, Polygon, Arbitrum, Base'
      },
      quickIntegration: {
        title: 'Quick Integration',
        description: 'Complete API documentation and SDK, support multiple programming languages, 30-minute integration'
      },
      security: {
        title: 'Signature Verification',
        description: 'MD5 signature verification, HTTPS encrypted transmission, multiple security guarantees'
      },
      realtime: {
        title: 'Real-time Status',
        description: 'Real-time order status push, webhook callback support, ensure timely transaction synchronization'
      },
      support: {
        title: '7x24 Support',
        description: 'Professional technical team 7x24 hours online support, rapid response to technical issues'
      }
    }
  },

  // Scenarios Section
  scenarios: {
    title: 'Use Cases',
    subtitle: 'Covering crypto payment needs across multiple industries',
    tabs: {
      ecommerce: 'Cross-border E-commerce',
      saas: 'SaaS Subscription',
      gaming: 'Gaming Assets',
      otc: 'OTC Trading'
    },
    content: {
      ecommerce: {
        title: 'Cross-border E-commerce',
        description: 'Solve crypto payment needs for cross-border e-commerce platforms',
        points: [
          'Support global users paying with stablecoins',
          'Avoid high fees from traditional bank transfers',
          'Achieve 7x24 instant settlement',
          'Support multiple mainstream stablecoin options'
        ]
      },
      saas: {
        title: 'SaaS Subscription',
        description: 'Provide flexible crypto subscription payments for SaaS products',
        points: [
          'Support monthly, yearly and other subscription models',
          'Automated subscription renewal management',
          'Reduce payment failure and chargeback risks',
          'Cover global user payment needs'
        ]
      },
      gaming: {
        title: 'Gaming & Digital Assets',
        description: 'Payment infrastructure for blockchain games and digital asset trading',
        points: [
          'Support in-game asset purchases and trading',
          'NFT marketplace payment integration',
          'Player deposit and withdrawal functions',
          'Multi-chain gaming asset interoperability'
        ]
      },
      otc: {
        title: 'OTC & Market Making',
        description: 'Professional payment solutions for OTC platforms and market makers',
        points: [
          'Security guarantee for large transactions',
          'Multi-signature wallet integration support',
          'Real-time exchange rate and slippage control',
          'Compliance reporting and transaction records'
        ]
      }
    }
  },

  // Integration Section
  integration: {
    title: 'Integration Process',
    subtitle: 'Complete CoolPay integration in four steps and start accepting cryptocurrency payments',
    steps: {
      0: {
        number: '01',
        title: 'Register Account',
        description: 'Create CoolPay account, get merchant ID and application credentials'
      },
      1: {
        number: '02',
        title: 'Configure Keys',
        description: 'Generate API key pairs, configure signature verification parameters'
      },
      2: {
        number: '03',
        title: 'Integrate API',
        description: 'Call payment gateway interface, integrate with existing systems'
      },
      3: {
        number: '04',
        title: 'Start Accepting',
        description: 'Launch cryptocurrency payment service, process user orders'
      }
    },
    cta: 'View Documentation',
    ready: 'Ready to start integration?',
    readyDesc: 'Check out complete technical documentation and API guides'
  },

  // Security Section
  security: {
    title: 'Security & Compliance',
    left: {
      title: 'Security Measures',
      items: {
        0: {
          title: 'HTTPS Encryption',
          description: 'All data transmission uses SSL/TLS encryption to ensure communication security'
        },
        1: {
          title: 'MD5 Signature Verification',
          description: 'All requests and callbacks require signature verification to prevent data tampering'
        },
        2: {
          title: 'Secure Key Storage',
          description: 'Multi-layer encryption storage with regular key rotation'
        },
        3: {
          title: 'Real-time Monitoring',
          description: '24-hour system monitoring with real-time alerts for abnormal transactions'
        }
      }
    },
    right: {
      title: 'Compliance Certification',
      description: 'Strictly adhere to international security standards and compliance requirements to ensure your business operates compliantly',
      iso: 'Security Certification',
      soc: 'Audit Report',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service'
    }
  },

  // FAQ Section
  faq: {
    title: 'FAQ',
    subtitle: 'Quickly get answers to common questions',
    items: {
      0: {
        question: 'How long does it take to integrate CoolPay?',
        answer: 'Typically, technical teams can complete basic integration within 1-2 business days. Including testing and launch, the entire process takes approximately 3-5 business days.'
      },
      1: {
        question: 'Which cryptocurrencies and blockchain networks are supported?',
        answer: 'Currently supports mainstream stablecoins like USDT and USDC, covering 15+ mainstream blockchain networks including Ethereum, BSC, Polygon, Arbitrum, Base, etc.'
      },
      2: {
        question: 'How are transaction fees charged?',
        answer: 'We use a tiered fee structure, offering different rate tiers based on monthly transaction volume. Please contact our business team for detailed pricing.'
      },
      3: {
        question: 'Do you provide a testing environment?',
        answer: 'Yes, we provide a complete testing environment (Testnet) where you can fully test all features before going live to ensure integration stability.'
      },
      4: {
        question: 'How can I get technical support?',
        answer: 'We provide 7x24 hour technical support services. You can contact our technical team through online customer service, technical QQ groups, or email.'
      },
      5: {
        question: 'How is fund security guaranteed?',
        answer: 'Multiple security measures including multi-signature technology, cold/hot wallet separation, real-time risk monitoring, and professional insurance coverage ensure fund security.'
      }
    },
    help: {
      title: 'Have other questions?',
      description: 'Contact our technical support team for assistance',
      button: 'Contact Support'
    }
  },

  // Partners Section
  partners: {
    title: 'Partners',
    subtitle: 'Building cryptocurrency payment ecosystem with leading global enterprises',
    categories: ['Crypto Exchanges', 'SaaS Platforms', 'Blockchain Gaming', 'OTC Institutions'],
    stats: {
      clients: 'Enterprise Clients',
      uptime: 'System Uptime',
      support: 'Technical Support'
    }
  },

  // Contact Section
  contact: {
    title: 'Contact Us',
    subtitle: 'Choose the most suitable way to contact us for professional support',
    business: {
      title: 'Business Partnership',
      email: 'business@cool-pay.link',
      description: 'Business cooperation, rate negotiation, partnership discussion'
    },
    support: {
      title: 'Technical Support',
      description: '7x24 Online Support',
      desc: 'For technical issues or assistance needed',
      button: 'Contact Support'
    },
    docs: {
      title: 'Developer Docs',
      description: 'API Documentation & Integration Guide',
      desc: 'Complete developer resources and documentation',
      button: 'View Documentation'
    },
    resources: {
      title: 'Developer Resources',
      description: 'Get complete API documentation, SDK downloads, and integration guides to quickly start your cryptocurrency payment integration journey',
      api: 'API Documentation',
      apiDesc: 'Complete REST API reference documentation',
      sdk: 'SDK Downloads',
      sdkDesc: 'Multi-language SDKs and code examples',
      guide: 'Integration Guide',
      guideDesc: 'Detailed integration steps and best practices',
      community: 'Developer Community',
      communityDesc: 'Exchange experiences with other developers'
    }
  },

  // Footer
  footer: {
    slogan: 'Providing secure and efficient cryptocurrency payment solutions for global enterprises',
    product: {
      title: 'Product',
      features: 'Features',
      scenarios: 'Scenarios',
      pricing: 'Pricing',
      security: 'Security'
    },
    resources: {
      title: 'Resources',
      api: 'API Docs',
      sdk: 'SDK Download',
      guide: 'Integration Guide',
      blog: 'Tech Blog'
    },
    company: {
      title: 'Company',
      about: 'About Us',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      contact: 'Contact Us'
    },
    copyright: '© 2024 cool-pay.link All rights reserved.',
    poweredBy: 'Website Builder'
  },

  // Language
  language: {
    en: 'English',
    zh: '中文'
  }
};

export default en;
